// packages import
import { Alert, Button, ButtonProps, CircularProgress, DialogActions, Typography } from '@mui/material'
import { FC } from 'react'

// component import
import { CustomModal } from './CustomModal'

interface ConfirmationModalProps {
  isOpen: boolean
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  isLoading?: boolean
  onConfirm: () => void
  onCancel: () => void
  variant?: 'danger' | 'warning' | 'info'
  showConfirmButton?: boolean
  /**
   * Failure from the confirmed action, rendered inline instead of as a toast. Server-side guards
   * explain what to do instead, and those explanations are too long for a snackbar — as a
   * full-width toast one covered the page header entirely. Shown here it stays next to the action
   * that caused it and can be read at leisure.
   */
  error?: string | null
}

const confirmButtonColors: Record<string, ButtonProps['color']> = {
  danger: 'primary',
  warning: 'warning',
  info: 'info'
}

export const ConfirmationModal: FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  onConfirm,
  onCancel,
  variant = 'info',
  showConfirmButton = true,
  error = null
}) => {
  return (
    <CustomModal title={title} isOpen={isOpen} handleClose={onCancel}>
      <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
        {message}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <DialogActions sx={{ padding: '20px 0 0', gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" disabled={isLoading}>
          {cancelText}
        </Button>

        {showConfirmButton && (
          <Button
            onClick={onConfirm}
            variant="contained"
            color={confirmButtonColors[variant]}
            disabled={isLoading}
            endIcon={isLoading && <CircularProgress size={20} color="inherit" />}
          >
            {confirmText}
          </Button>
        )}
      </DialogActions>
    </CustomModal>
  )
}
